import React from 'react'
import { makeStyles } from 'tss-react/mui'
import LanguageIcon from '@mui/icons-material/Language'
import { Box, Button, Popover } from '@mui/material'
import type { Theme } from '@mui/material/styles'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    borderRadius: '4px',
    left: theme.spacing(2),
    bottom: theme.spacing(2),
  },
  button: {
    color: theme.palette.text.primary,
    padding: theme.spacing(1, 1.25, 1, 1.5),
    fontSize: 12,
    letterSpacing: 0,
    boxShadow: '0 0 2px 0 rgb(0 0 0 / 8%), 0 1px 2px 0 rgb(0 0 0 / 10%)',
  },
  icon: {
    color: 'rgba(39,49,59,0.7)',
  },
  menu: {
    boxShadow: '0 0 2px 0 rgb(0 0 0 / 8%), 0 1px 2px 0 rgb(0 0 0 / 10%)',
  },
  menuItem: {
    borderRadius: 0,
    display: 'block',
    color: 'rgba(39,49,59,0.7)',
    width: 108,
    boxShadow: 'none',
    textAlign: 'left',
    paddingLeft: theme.spacing(3.5),
    '&:hover': {
      background: 'rgb(222,229,236)',
    },
    '&:before': {
      top: 'calc(50% - 4px)',
      borderRadius: '50%',
      position: 'absolute',
      content: '""',
      height: theme.spacing(1),
      width: theme.spacing(1),
      left: theme.spacing(1.5),
    },
  },
  selected: {
    color: theme.palette.text.primary,
    background: 'rgb(222,229,236)',
    '&:before': {
      background: '#40b87c',
    },
  },
}))

export type Lang = {
  value?: string
  title: string
}

const LangSwitch: React.FC<{
  locale: Lang['value']
  locales: Lang[]
  onSelectLanguage: (locale: Lang['value']) => void
}> = ({ locale, locales, onSelectLanguage }) => {
  const { classes, cx } = useStyles()
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const currentLang = locales.find(({ value }) => value === locale)

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClickLanguage = (locale: Lang['value']) => {
    onSelectLanguage(locale)
    handleClose()
  }

  return (
    <Box className={classes.root}>
      <Button
        variant="text"
        className={classes.button}
        startIcon={<LanguageIcon className={classes.icon} />}
        onClick={handlePopoverOpen}
      >
        {currentLang?.title}
      </Button>
      <Popover
        className={classes.menu}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {locales.map(({ value, title }) => {
          const selected = value === locale
          return (
            <Button
              className={cx(
                classes.button,
                classes.menuItem,
                selected && classes.selected
              )}
              variant="text"
              key={value}
              onClick={() => handleClickLanguage(value)}
              // selected={selected}
            >
              {title}
            </Button>
          )
        })}
      </Popover>
    </Box>
  )
}

export default LangSwitch
